import React from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import * as Yup from 'yup';
import { Modal, Button, Banner, ButtonGroup, Input } from '@chaser/components';
import usePush from '../../../../hooks/usePush';
import LabeledFieldWithErrorLabel from '../../../../components/LabeledFieldWithErrorLabel/index.jsx';
import { useHistory } from 'react-router';
import ConnectService from '../../../../services/connect-service';
import { IntegrationDisplayNames, Integrations } from '../../../../util/constants';
import { pendo } from '../../../../util/logging';

const OpenAPIModal = ({ open, close, onClosed, sandbox, integrationType }) => {
  const history = useHistory();
  const { execute, loading } = usePush(ConnectService.connectOpenAPI, {
    errorToast: 'Failed to connect',
  });

  const form = useFormik({
    initialValues: {
      name: '',
    },
    validate: ({ name }) => {
      const errors = {};
      if (!name) errors.name = 'Please enter a company name!';
      return errors;
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .required('You must set a name')
        .max(100)
        .min(5),
    }),
    onSubmit: async ({ name }, { setSubmitting }) => {
      const [err, res] = await execute({
        name,
        integrationType,
        sandbox,
      });
      setSubmitting(false);
      if (err) return;
      close();
      pendo.track('integration_modal_submitted', {
        integration: integrationType,
        isSandbox: !!sandbox,
      });
      history.push(`/?organisation=${res.ShortCode}`);
    },
  });

  const modalTitle = sandbox
    ? 'Create demo organisation'
    : `Connect to ${IntegrationDisplayNames[integrationType]}`;

  const buttonText = sandbox ? 'Create' : 'Connect';

  const isSage50 = integrationType === Integrations.SAGE50;

  return (
    <Modal title={modalTitle} open={open} toggle={close} onClosed={onClosed}>
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            {isSage50 && (
              <Banner intent="info" marginBottom={24} showCloseButton={false}>
                This integration works with <b>Sage 50 Accounts UK </b> only. If you’re using{' '}
                <b>Sage 50 US</b>, you can connect using the&nbsp;
                <a href="https://help.chaserhq.com/chase-import-csv-uploader">
                  <u>CSV</u>
                </a>
                &nbsp;option
              </Banner>
            )}

            <LabeledFieldWithErrorLabel
              name="name"
              as={Input}
              label="Company name"
              width="100%"
              placeholder="Enter name"
            />
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button disabled={!form.isValid} loading={loading} type="submit" data-trackingid={`integration-${integrationType}-connect`}>
                {buttonText}
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default OpenAPIModal;
